import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { ActionListData, ActionListItem } from '../../../models/message.types';
import { AiChatActionHandlerService } from '../../../services';

@Component({
  selector: 'qt-ai-chat-action-list',
  standalone: true,
  imports: [MatIconModule, TranslationCoreModule],
  template: `
    @if (filteredItems().length) {
      <div class="ai-chat-action-list">
        <div class="ai-chat-action-list__header">
          <mat-icon class="ai-chat-action-list__icon">list_alt</mat-icon>
          <span class="ai-chat-action-list__title">{{
            'GNR_AVAILABLE_ACTIONS' | translate
          }}</span>
        </div>
        <div class="ai-chat-action-list__items">
          @for (item of filteredItems(); track trackByItem($index, item)) {
            <div
              class="ai-chat-action-list__item"
              (click)="navigateToItem(item.url)">
              <div class="ai-chat-action-list__item-content">
                <h4 class="ai-chat-action-list__item-name">{{ item.name }}</h4>
                @if (item.description) {
                  <p class="ai-chat-action-list__item-description">
                    {{ item.description }}
                  </p>
                }
              </div>
              <mat-icon class="ai-chat-action-list__item-arrow"
                >arrow_forward_ios</mat-icon
              >
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['./ai-chat-action-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatActionListComponent {
  // Signal-based input
  readonly data = input.required<ActionListData>();

  private readonly actionHandler = inject(AiChatActionHandlerService);

  readonly filteredItems = computed(() =>
    (this.data().itemList ?? []).filter(item => this.isValidUrl(item?.url))
  );

  trackByItem = (index: number, item: ActionListItem): string => item.url;

  private isValidUrl(url: string | undefined | null): boolean {
    return !!url && url.startsWith('/') && url.includes('?prevUrl=');
  }

  navigateToItem(url: string): void {
    if (!this.isValidUrl(url)) return;

    const [pathPart, queryPart] = url.split('?');
    const searchParams = new URLSearchParams(queryPart ?? '');
    searchParams.set('from', 'ai-chat');
    const serializedParams = searchParams.toString();
    const targetUrl = serializedParams
      ? `${pathPart}?${serializedParams}`
      : pathPart;

    this.actionHandler.navigateToUrl(targetUrl);
  }
}
